import { useTranslation } from 'react-i18next';
import { NegotiationParams } from '../../../components/dialogs';
import { H3 } from '../../../components/typography';
import { Box, Grid, Stack, Divider, Chip } from '@mui/material';
import { InfoItem } from '../../../components/misc/InfoItem';
import { AutoAwesome, Euro, Settings } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface NegotiationDetailsProps {
  negotiation: NegotiationParams;
  strategy: string;
}

const formatPrice = (value: number | string | undefined): string => {
  if (value === undefined || value === null || value === '') return 'N/A';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return 'N/A';
  return `€${numValue.toLocaleString(window.navigator.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const NegotiationDetails = ({
  negotiation,
  strategy
}: NegotiationDetailsProps) => {

  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 2}}>
      <Grid container spacing={3}>
        {/* Strategy Section */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{
            height: '100%',
            p: 2.5,
            borderRadius: 2,
            border: (theme: any) => theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 0, 0, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <AutoAwesome sx={{
                  fontSize: '1.25rem',
                  color: (theme: any) => theme.palette.mode === 'dark'
                    ? 'rgba(124, 77, 255, 0.9)'
                    : 'rgba(124, 77, 255, 0.8)',
                }} />
              </Box>
              <H3 sx={{ mb: 0, fontSize: '1.125rem', fontWeight: 600 }}>
                {t('Strategy')}
              </H3>
            </Stack>
            <Divider sx={{
              borderColor: (theme: any) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.08)',
            }} />
            <InfoItem title={t('Negotiation Strategy')} value={strategy} />
          </Box>
        </Grid>

        {/* Prices Section */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{
            height: '100%',
            p: 2.5,
            borderRadius: 2,
            border: (theme: any) => theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 0, 0, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Euro sx={{
                  fontSize: '1.25rem',
                  color: (theme: any) => theme.palette.mode === 'dark'
                    ? 'rgba(76, 175, 80, 0.9)'
                    : 'rgba(76, 175, 80, 0.8)',
                }} />
              </Box>
              <H3 sx={{ mb: 0, fontSize: '1.125rem', fontWeight: 600 }}>
                {t('Prices')}
              </H3>
            </Stack>
            <Divider sx={{
              borderColor: (theme: any) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.08)',
            }} />
            <InfoItem title={t('Estimated Price')} value={formatPrice(negotiation.estimatedPrice)} />
            <InfoItem title={t('Fair Price')} value={formatPrice(negotiation.fairPrice)} />
            <InfoItem title={t('Minimum Acceptance')} value={formatPrice(negotiation.minimumAcceptance)} />
            <InfoItem title={t('Target Price')} value={formatPrice(negotiation.targetPrice)} />
          </Box>
        </Grid>

        {/* Settings Section */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{
            height: '100%',
            p: 2.5,
            borderRadius: 2,
            border: (theme: any) => theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 0, 0, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Settings sx={{
                  fontSize: '1.25rem',
                  color: (theme: any) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 152, 0, 0.9)'
                    : 'rgba(255, 152, 0, 0.8)',
                }} />
              </Box>
              <H3 sx={{ mb: 0, fontSize: '1.125rem', fontWeight: 600 }}>
                {t('Settings')}
              </H3>
            </Stack>
            <Divider sx={{
              borderColor: (theme: any) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.08)',
            }} />
            <Box sx={{ pt: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Box component="span" sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: (theme: any) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.6)'
                    : 'rgba(0, 0, 0, 0.6)',
                }}>
                  {t('Auto Response')}
                </Box>
              </Stack>
              <Chip
                label={negotiation.autoRespond ? t('Yes') : t('No')}
                size="small"
                sx={{
                  height: 24,
                  borderRadius: 1,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  bgcolor: negotiation.autoRespond
                    ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.12)')
                    : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'),
                  color: negotiation.autoRespond
                    ? (theme.palette.mode === 'dark' ? '#9BE7B0' : '#2e7d32')
                    : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'),
                  border: '1px solid',
                  borderColor: negotiation.autoRespond
                    ? (theme.palette.mode === 'dark' ? 'rgba(76,175,80,0.25)' : 'rgba(76,175,80,0.2)')
                    : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'),
                }}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NegotiationDetails;